import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What format does the playbook come in?',
    answer: 'The Full Playbook is delivered as a beautifully designed PDF — ready to read on any device (phone, tablet, laptop, or desktop). No special software needed. Just open and use.',
  },
  {
    question: 'How is it delivered after purchase?',
    answer: "Instantly. The moment your payment goes through, you'll receive an email with your download link. Most families are reading within 60 seconds of checkout.",
  },
  {
    question: 'Is this only for football, or does it work for other sports?',
    answer: 'It works for every NCAA sport — football, basketball, baseball, softball, soccer, swimming, track, volleyball, lacrosse, and more. The recruiting process follows the same framework regardless of the sport.',
  },
  {
    question: 'My athlete is a freshman/sophomore — is it too early?',
    answer: "It's actually the perfect time. Coaches start building their boards earlier than most families realize. The playbook includes a full freshman-to-senior roadmap so you know exactly what to do at every stage.",
  },
  {
    question: "What if I'm not happy with the playbook?",
    answer: "You're fully covered by our 30-day money-back guarantee. If you read it and don't feel it was worth every penny, just email us and we'll refund you — no questions, no hassle, no hard feelings.",
  },
  {
    question: 'How is this different from free stuff I can find online?',
    answer: 'The internet is full of scattered, generic advice. The Full Playbook is a complete, step-by-step system — templates, trackers, scripts, and roadmaps all in one place, designed specifically for families who want results, not just information.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="bg-[#0a1628] pb-20 md:pb-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#f5a623]/10 border border-[#f5a623]/30 rounded-full px-4 py-2 mb-5">
            <i className="ri-question-answer-fill text-[#f5a623] text-sm" />
            <span className="text-[#f5a623] font-body text-xs font-semibold uppercase tracking-widest">Common Questions</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white uppercase leading-tight">
            Got Questions?
            <span className="block text-[#f5a623]">We've Got Answers.</span>
          </h2>
        </div>

        {/* Accordion */}
        <div className="flex flex-col gap-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                  isOpen
                    ? 'border-[#f5a623]/50 bg-[#122040]'
                    : 'border-white/10 bg-[#0f1e3a] hover:border-white/20'
                }`}
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                >
                  <span
                    className={`font-body text-sm md:text-base font-semibold leading-snug transition-colors duration-200 ${
                      isOpen ? 'text-[#f5a623]' : 'text-white'
                    }`}
                  >
                    {faq.question}
                  </span>
                  <div
                    className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-200 ${
                      isOpen ? 'bg-[#f5a623]/20 text-[#f5a623] rotate-180' : 'bg-white/10 text-white/50'
                    }`}
                  >
                    <i className="ri-arrow-down-s-line text-lg" />
                  </div>
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="px-6 pb-5 font-body text-sm text-white/65 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom nudge */}
        <p className="text-center font-body text-sm text-white/40 mt-10">
          Still have a question?{' '}
          <a
            href="mailto:support@recruitingplaybook.com"
            className="text-[#f5a623] hover:underline cursor-pointer"
          >
            Email us anytime.
          </a>
        </p>
      </div>
    </section>
  );
}
